import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Shield, Globe } from 'lucide-react';
import HistoryVerdict from './HistoryVerdict';

const GameWin = ({ gameState }) => {
  const navigate = useNavigate();
  const [showVerdict, setShowVerdict] = useState(false);
  const nbAnnees = gameState.year ? gameState.year - 2030 : 0;

  const FloatingParticles = ({ color = 'blue' }) => {
    const colorMap = {
      blue: 'bg-blue-400',
      green: 'bg-green-400',
      red: 'bg-red-400',
      orange: 'bg-orange-400'
    };

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 ${colorMap[color]} rounded-full animate-ping`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>
    );
  };

  // Animation Components
  const MilitaryParade = () => {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute bottom-4"
            style={{
              left: `-10%`,
              animationDelay: `${i * 0.8}s`,
              animation: `marching 12s linear infinite`
            }}
          >
            <div className="flex items-center space-x-2">
              <Shield
                className="text-blue-400 animate-bounce"
                size={24}
                style={{
                  animationDelay: `${i * 0.2}s`,
                  filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))'
                }}
              />
              <div className="w-6 h-2 bg-blue-500 rounded-full" />
            </div>
          </div>
        ))}
        <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r from-blue-900 via-blue-700 to-blue-900 animate-pulse" />
        <style jsx>{`
          @keyframes marching {
            0% { left: -10%; }
            100% { left: 110%; }
          }
        `}</style>
      </div>
    );
  };

  if (showVerdict) {
    return <HistoryVerdict gameState={gameState} />;
  }

  return (
    <div className="relative w-full h-screen bg-black">
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center overflow-hidden">
        <FloatingParticles color="blue" />
        <MilitaryParade />

        <div className="relative z-10 text-center p-8 bg-black/40 backdrop-blur-sm rounded-3xl border border-blue-500/30 max-w-2xl mx-4">
          <div className="mb-8">
            <Trophy className="w-24 h-24 text-yellow-400 mx-auto mb-4 animate-bounce"
              style={{ filter: 'drop-shadow(0 0 20px rgba(250, 204, 21, 0.6))' }} />
            <Shield className="w-16 h-16 text-blue-400 mx-auto animate-pulse" />
          </div>

          <h1 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-4 animate-pulse">
            VICTOIRE MILITAIRE
          </h1>

          <p className="text-xl text-blue-200 mb-8 leading-relaxed">
            Votre stratégie militaire a triomphé sur le champ de bataille mondial.
            Les nations s'inclinent devant votre puissance tactique supérieure.
          </p>

          <div className="grid grid-cols-3 gap-6 text-center">
            <div className="bg-blue-900/50 p-4 rounded-xl">
              <div className="text-3xl font-bold text-blue-300">{gameState.joueur?.population ?? 0}</div>
              <div className="text-blue-200 text-sm">Population Restante</div>
            </div>
            <div className="bg-blue-900/50 p-4 rounded-xl">
              <div className="text-3xl font-bold text-orange-300">
                +{gameState.climate?.degree ?? 1.5}°C
              </div>
              <div className="text-blue-200 text-sm">Degrés</div>
            </div>
            <div className="bg-blue-900/50 p-4 rounded-xl">
              <div className="text-3xl font-bold text-yellow-300">{nbAnnees}</div>
              <div className="text-blue-200 text-sm">Années de Règne</div>
            </div>
          </div>

          <div className="relative z-10 mt-8 mb-12 flex flex-col gap-4 items-center">
            <button
              onClick={() => setShowVerdict(true)}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl font-bold text-white shadow-lg hover:scale-105 transition-transform flex items-center gap-3"
            >
              <Trophy className="w-6 h-6" />
              Voir le Jugement de l'Histoire
            </button>
            <button
              onClick={() => navigate('/review')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl font-bold text-white shadow-lg hover:scale-105 transition-transform flex items-center gap-3"
            >
              <Globe className="w-6 h-6" />
              Revoir la carte et les journaux
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameWin;
